import { isEqual, sum } from 'lodash';
import { rotatePointWithFocus } from '@/utils/math-kit';
import type { Border, Cell, IPoint, ITableLayer, ITableLayerProps } from '@/version-types';
import { getCellStyle } from './cell-style';

export interface TableCellRect {
    cell: Cell;
    id: string;
    hIndex: number;
    vIndex: number;
    left: number;
    top: number;
    width: number;
    height: number;
}

interface TableBorderLine {
    border: Border;
    direction: 'horizontal' | 'vertical';
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

export interface TableCellBorderInsets {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

type TableTransform = Pick<ITableLayer, 'left' | 'top' | 'width' | 'height' | 'rotate'>;

/** 表格本地左上角经过中心旋转后，在当前父坐标系中的位置。 */
export const getTableVisualOrigin = (layer: TableTransform): IPoint => {
    const centerPoint = {
        x: layer.left + layer.width / 2,
        y: layer.top + layer.height / 2,
    };
    return rotatePointWithFocus({ x: layer.left, y: layer.top }, centerPoint, layer.rotate);
};

/** 平移候选表格，使其旋转后的本地左上角与固定锚点重合。 */
export const alignTableToVisualOrigin = <T extends TableTransform>(layer: T, visualOrigin: IPoint): T => {
    const candidateOrigin = getTableVisualOrigin(layer);
    layer.left += visualOrigin.x - candidateOrigin.x;
    layer.top += visualOrigin.y - candidateOrigin.y;
    return layer;
};

const getTableGridOffsets = (sizes: number[]) => [
    ...sizes.map((_, index) => sizes.slice(0, index).reduce((a, b) => a + b, 0)),
    sum(sizes),
];

export const getTableOuterInsets = (
    tableLayerData: Pick<ITableLayerProps, 'allStyleId' | 'cells' | 'baseCells' | 'styles' | 'presetId'>,
) => {
    const cells = tableLayerData.cells.flat().filter(Boolean) as Cell[];
    const colCount = tableLayerData.baseCells.horizontal.length;
    const rowCount = tableLayerData.baseCells.vertical.length;
    const getMaxBorderWidth = (side: 'top' | 'right' | 'bottom' | 'left', isOuterCell: (cell: Cell) => boolean) =>
        cells.reduce((maxWidth, cell) => {
            if (!isOuterCell(cell)) return maxWidth;
            return Math.max(maxWidth, getCellStyle(cell, tableLayerData).border?.[side]?.width ?? 0);
        }, 0) / 2;

    return {
        top: getMaxBorderWidth('top', (cell) => cell.vIndex === 0),
        right: getMaxBorderWidth('right', (cell) => cell.hIndex + cell.colSpan === colCount),
        bottom: getMaxBorderWidth('bottom', (cell) => cell.vIndex + cell.rowSpan === rowCount),
        left: getMaxBorderWidth('left', (cell) => cell.hIndex === 0),
    };
};

export const getTableCellRects = (layer: ITableLayer): TableCellRect[] => {
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        cells: cellsProp,
    } = layer.data;
    const fakeBorder = getTableOuterInsets(layer.data);
    const lefts = getTableGridOffsets(horizontalSizes);
    const tops = getTableGridOffsets(verticalSizes);

    return (cellsProp.flat().filter(Boolean) as Cell[]).map((cell) => {
        const left = fakeBorder.left + lefts[cell.hIndex];
        const top = fakeBorder.top + tops[cell.vIndex];
        const right = fakeBorder.left + lefts[cell.hIndex + cell.colSpan];
        const bottom = fakeBorder.top + tops[cell.vIndex + cell.rowSpan];

        return {
            cell,
            id: cell.__renderId,
            hIndex: cell.hIndex,
            vIndex: cell.vIndex,
            left,
            top,
            width: right - left,
            height: bottom - top,
        };
    });
};

export const getTableBorderLines = ({
    data: {
        allStyleId,
        cells: cellsProp,
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        styles,
        presetId,
    },
}: Pick<ITableLayer, 'width' | 'height' | 'data'>): TableBorderLine[] => {
    const fakeBorder = getTableOuterInsets({
        allStyleId,
        cells: cellsProp,
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        styles,
        presetId,
    });
    const lefts = getTableGridOffsets(horizontalSizes);
    const tops = getTableGridOffsets(verticalSizes);
    const lines: TableBorderLine[] = [];
    const appendLine = (line: TableBorderLine) => {
        if (!line.border || line.border.width <= 0 || line.border.color === '#00000000') return;
        const sameLine = lines.find(
            (item) =>
                item.direction === line.direction &&
                isEqual(item.border, line.border) &&
                item.startX === line.startX &&
                item.startY === line.startY &&
                item.endX === line.endX &&
                item.endY === line.endY,
        );
        if (!sameLine) {
            lines.push(line);
        }
    };
    const getLine = ({
        direction,
        hIndex,
        vIndex,
        span,
        border,
    }: {
        direction: 'horizontal' | 'vertical';
        hIndex: number;
        vIndex: number;
        span: number;
        border: Border;
    }): TableBorderLine => {
        let startX = fakeBorder.left + lefts[hIndex];
        if (hIndex === 0 && direction === 'horizontal') startX -= fakeBorder.left;
        let startY = fakeBorder.top + tops[vIndex];
        if (vIndex === 0 && direction === 'vertical') startY -= fakeBorder.top;
        let endX = direction === 'horizontal' ? fakeBorder.left + lefts[hIndex + span] : startX;
        if (hIndex + span === horizontalSizes.length && direction === 'horizontal') endX += fakeBorder.right;
        let endY = direction === 'vertical' ? fakeBorder.top + tops[vIndex + span] : startY;
        if (vIndex + span === verticalSizes.length && direction === 'vertical') endY += fakeBorder.bottom;

        return {
            border,
            direction,
            startX,
            startY,
            endX,
            endY,
        };
    };

    cellsProp.forEach((trCells, vIndex) => {
        trCells.forEach((cell, hIndex) => {
            if (!cell) return;
            const style = getCellStyle(cell, { allStyleId, styles, presetId });
            if (!style.border) return;

            appendLine(
                getLine({
                    direction: 'horizontal',
                    hIndex,
                    vIndex,
                    span: cell.colSpan,
                    border: style.border.top,
                }),
            );
            appendLine(
                getLine({
                    direction: 'vertical',
                    hIndex,
                    vIndex,
                    span: cell.rowSpan,
                    border: style.border.left,
                }),
            );
            if (vIndex + cell.rowSpan === verticalSizes.length) {
                appendLine(
                    getLine({
                        direction: 'horizontal',
                        hIndex,
                        vIndex: vIndex + cell.rowSpan,
                        span: cell.colSpan,
                        border: style.border.bottom,
                    }),
                );
            }
            if (hIndex + cell.colSpan === horizontalSizes.length) {
                appendLine(
                    getLine({
                        direction: 'vertical',
                        hIndex: hIndex + cell.colSpan,
                        vIndex,
                        span: cell.rowSpan,
                        border: style.border.right,
                    }),
                );
            }
        });
    });

    return lines.sort((a, b) => a.direction.charCodeAt(0) - b.direction.charCodeAt(0));
};

/** 获取单元格背景需要避让的最终可见边框内半宽。 */
export const getTableCellBorderInsets = (layer: ITableLayer, rect: TableCellRect): TableCellBorderInsets => {
    const rectRight = rect.left + rect.width;
    const rectBottom = rect.top + rect.height;
    const insets: TableCellBorderInsets = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    };
    const overlaps = (startA: number, endA: number, startB: number, endB: number) =>
        Math.max(startA, startB) < Math.min(endA, endB);
    const isSameCoordinate = (a: number, b: number) => Math.abs(a - b) < 1e-6;

    getTableBorderLines(layer).forEach((line) => {
        const inset = line.border.width / 2;
        if (line.direction === 'horizontal') {
            if (!overlaps(line.startX, line.endX, rect.left, rectRight)) return;
            if (isSameCoordinate(line.startY, rect.top)) insets.top = Math.max(insets.top, inset);
            if (isSameCoordinate(line.startY, rectBottom)) insets.bottom = Math.max(insets.bottom, inset);
            return;
        }

        if (!overlaps(line.startY, line.endY, rect.top, rectBottom)) return;
        if (isSameCoordinate(line.startX, rect.left)) insets.left = Math.max(insets.left, inset);
        if (isSameCoordinate(line.startX, rectRight)) insets.right = Math.max(insets.right, inset);
    });

    return insets;
};

export const getTableCellInfoListByPoint = ({ point, tableLayer }: { point: IPoint; tableLayer: ITableLayer }) => {
    const localPoint = getTableLocalPointByScenePoint({ point, tableLayer });

    return getTableCellRects(tableLayer)
        .filter(
            (rect) =>
                localPoint.x >= rect.left &&
                localPoint.x <= rect.left + rect.width &&
                localPoint.y >= rect.top &&
                localPoint.y <= rect.top + rect.height,
        )
        .map((rect) => ({
            id: rect.id,
            hIndex: rect.hIndex,
            vIndex: rect.vIndex,
        }));
};

export const getTableLocalPointByScenePoint = ({ point, tableLayer }: { point: IPoint; tableLayer: ITableLayer }) => {
    const tableCenterPoint = {
        x: tableLayer.left + tableLayer.width / 2,
        y: tableLayer.top + tableLayer.height / 2,
    };
    const pointWithoutRotate = rotatePointWithFocus(point, tableCenterPoint, -tableLayer.rotate);

    return {
        x: pointWithoutRotate.x - tableLayer.left,
        y: pointWithoutRotate.y - tableLayer.top,
    };
};

export const getCellStyleForRender = (
    cell: Cell,
    tableLayerData: Pick<ITableLayerProps, 'allStyleId' | 'styles' | 'presetId'>,
) => getCellStyle(cell, tableLayerData);
